// ----------------------------------------------------
// 📚 CREATE LIBRARY — TITLE + AUTO SLUG + SEO
// ----------------------------------------------------

"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";

export default function CreateLibrary() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  // SEO Fields
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  // Auto Slug
  useEffect(() => {
    if (!slugEdited) {
      const generated = title
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-");
      setSlug(generated);
    }
  }, [title]);

  const handleSlugChange = (value: string) => {
    setSlug(value);
    setSlugEdited(true);
  };

  // CREATE LIBRARY
  const createLibrary = async () => {
    if (!title.trim()) return alert("Please enter library title");
    if (!slug.trim()) return alert("Slug cannot be empty");

    const ref = doc(fireDB, "NewBlogs", slug);
    const exists = await getDoc(ref);

    if (exists.exists()) {
      alert("This slug already exists!");
      return;
    }

    setSaving(true);

    try {
      await setDoc(ref, {
        title,
        slug,
        createdAt: Timestamp.now(),

        // 🌐 SEO Values
        metaTitle: metaTitle || title,
        metaDescription,
        metaKeywords: metaKeywords ? metaKeywords.split(",").map((k) => k.trim()) : [],
        canonicalUrl: `https://greatmastersai.com/${slug}`,
      });

      alert("🎉 Library Created Successfully!");
      navigate(`/createLibrary/${slug}/create-book`);
    } catch (err: any) {
      alert("Error: " + err.message);
    }

    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center p-6">
      <div className="w-full max-w-2xl bg-white shadow-2xl p-10 rounded-2xl border border-gray-200">

        <h1 className="text-4xl font-extrabold mb-6 text-center text-indigo-700 drop-shadow-sm">
          Create New Library 📚
        </h1>

        <div className="space-y-6">

          {/* TITLE */}
          <div>
            <label className="font-semibold text-gray-700">Library Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setSlugEdited(false);
              }}
              className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter library title..."
            />
          </div>

          {/* SLUG */}
          <div>
            <label className="font-semibold text-gray-700">Slug *</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              URL: <b>https://greatmastersai.com/{slug || "..."}</b>
            </p>
          </div>

          {/* SEO Meta Title */}
          <div>
            <label className="font-semibold text-gray-700">Meta Title</label>
            <input
              type="text"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-500"
              placeholder="SEO Title"
            />
          </div>

          {/* Meta Description */}
          <div>
            <label className="font-semibold text-gray-700">Meta Description</label>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-500"
              rows={3}
              placeholder="Short SEO description"
            />
          </div>

          {/* Keywords */}
          <div>
            <label className="font-semibold text-gray-700">Keywords (comma separated)</label>
            <input
              type="text"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-500"
              placeholder="blog, tutorial, guide"
            />
          </div>

          {/* BUTTON */}
          <button
            onClick={createLibrary}
            disabled={saving}
            className={`w-full py-3 rounded-xl text-white font-semibold transition-all text-lg shadow-lg
              ${saving ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-700 scale-[1.02]"}`}
          >
            {saving ? "Saving..." : "Create Library"}
          </button>
        </div>
      </div>
    </div>
  );
}
